import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Fragment, useState } from 'react'
import { FlipCard } from '~/components/ui/FlipCard'
import { ExplorerIcon } from './ExplorerIcon'
import solanaIcon from '../assets/solana.webp'

interface CardHeaderProps {
    name: string
    rank: number
    setTurns: React.Dispatch<React.SetStateAction<number>>
}

interface CardImageProps {
    image_url: string
}

interface CardTraitsProps {
    traits: MarketPlaceItemTraits[]
}

interface CardExplorerIconsProps {
    mint: string
}

interface CardDetailsProps {
    mint: string
    holder_wallet?: string
}

interface CardPriceProps {
    mint: string
    price?: number
}

function CardHeader({ name, rank, setTurns }: CardHeaderProps) {
    return (
        <div className='flex flex-row items-center text-[1em] font-bold p-[1em] gap-[1em]'>
            <div className='whitespace-nowrap overflow-hidden overflow-ellipsis flex-1 uppercase' title={name}>
                {name}
            </div>
            <div className='bg-[#9251FF] rounded-xl px-[1em] text-[0.8em]' title='rank'>
                {rank}
            </div>
            <FontAwesomeIcon
                icon={faArrowsRotate}
                className='cursor-pointer text-[1.2em]'
                onClick={() => setTurns((t) => t + 1)}
            />
        </div>
    )
}

function CardImage({ image_url }: CardImageProps) {
    return (
        <div style={{ aspectRatio: '1/1' }}>
            <img
                src={image_url}
                alt=''
                className='object-cover w-full h-full'
                loading='lazy'
                width={288}
                height={288}
            />
        </div>
    )
}

function CardTraits({ traits }: CardTraitsProps) {
    return (
        <div className='w-full h-full grid grid-cols-2 overflow-y-auto scrollbar-tiny text-[1em] p-[1em] gap-[0.5em] font-bold'>
            {traits?.map((trait) => (
                <Fragment key={trait.type}>
                    <div>{trait.type}</div>
                    <div className='text-right'>{trait.value}</div>
                </Fragment>
            ))}
        </div>
    )
}

/**
 * Hidden below `sm`. Three explorer links per card, at three cards a screen, is a lot of chrome
 * competing with the artwork and the price — and at phone scale they are ~17px targets nobody can
 * hit deliberately. The links stay one tap away on the item's own page.
 */
function CardExplorerIcons({ mint }: CardExplorerIconsProps) {
    return (
        <div className='hidden sm:flex flex-row items-center justify-center text-[1.5em] gap-[1em] p-[0.5em]'>
            <ExplorerIcon.Solscan mint={mint} />
            <ExplorerIcon.SolanaExplorer mint={mint} />
            <ExplorerIcon.MagicEden mint={mint} />
        </div>
    )
}

function CardDetails({ mint, holder_wallet }: CardDetailsProps) {
    const mintPreview = `${mint.slice(0, 4)}...${mint.slice(-4)}`
    // The holder is shown as a truncated wallet and never as a BR1 username. The
    // collection endpoint is unauthenticated and pages the whole collection, so
    // serving the name turned it into a public map from account to wallet. This was
    // already the display for any holder without a linked account.
    const holder = holder_wallet ? `${holder_wallet.slice(0, 4)}...${holder_wallet.slice(-4)}` : 'N/A'
    return (
        <div className='grid grid-cols-2 text-[1em] p-[1em] gap-[1em] font-bold'>
            <div>MINT</div>
            <div className='text-right overflow-hidden text-ellipsis tracking-wider' title={mint}>
                {mintPreview}
            </div>
            <div>HOLDER</div>
            <div className='text-right overflow-hidden text-ellipsis tracking-wider' title={holder_wallet}>
                {holder}
            </div>
        </div>
    )
}

function CardPrice({ mint, price }: CardPriceProps) {
    return (
        <div className='grid grid-cols-2 text-[1em] p-[1em] gap-[1em] font-bold'>
            <div className='flex flex-row items-center gap-[0.5em]'>
                {price && <img src={solanaIcon} className='h-[0.75em] w-[0.75em]' />}
                {price}
            </div>
            <a
                href={`https://magiceden.io/item-details/${mint}`}
                target='_blank'
                rel='noopener noreferrer'
                className='place-self-end px-[2em] py-[0.5em] rounded uppercase bg-[linear-gradient(100deg,rgb(201,31,255)_-15%,rgb(3,206,255)_170%)] hover:outline'
            >
                BUY
            </a>
        </div>
    )
}

function Card({ item }: { item: MarketplaceItem }) {
    const [turns, setTurns] = useState(0)
    const { name, rank, image_url, traits, mint, holder_wallet, price } = item
    return (
        <div className='container-inline w-full border border-white/20 rounded'>
            {/* Floored. Everything inside is sized in `em` off this one value, so a bare `4cqi`
                took the whole card down with the container: in the two-up phone grid the card is
                ~165px wide, which is a 6.6px base. `max()` keeps it legible while still letting the
                card scale up on a wide monitor. */}
            <div className='text-[max(0.8rem,4cqi)]'>
                <CardHeader name={name} rank={rank} setTurns={setTurns} />
                <div className='relative' style={{ aspectRatio: '1/1' }}>
                    <FlipCard
                        turns={turns}
                        front={<CardImage image_url={image_url} />}
                        back={<CardTraits traits={traits} />}
                    />
                </div>
                <CardExplorerIcons mint={mint} />
                <CardDetails mint={mint} holder_wallet={holder_wallet} />
                <CardPrice mint={mint} price={price} />
            </div>
        </div>
    )
}

export { Card }
